import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * Small "Add to Home Screen" banner.
 *
 * Chrome/Android fires `beforeinstallprompt` when the PWA is installable
 * (manifest + service worker + HTTPS + first-visit heuristic). We stash the
 * event and show a subtle bottom-of-screen banner that lets the user install
 * with one tap. If dismissed, we suppress it for 14 days via localStorage.
 *
 * iOS Safari does NOT fire this event — for iPhone users the app is still
 * installable via Share → "Add to Home Screen" (thanks to the apple-touch-icon
 * + apple-mobile-web-app-capable meta tags), but there's no programmatic way
 * to show the prompt. We handle that with a one-off hint on iOS.
 */
const DISMISS_KEY = "ps-pwa-dismissed-until";
const DISMISS_DAYS = 14;

function isIOS() {
  const ua = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua) && !window.MSStream;
}
function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}
function isDismissed() {
  try {
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return until > Date.now();
  } catch {
    return false;
  }
}

// The depot team is Dutch — showing Dutch iOS terminology ("Deel" / "Zet op
// beginscherm") when the browser locale starts with `nl` avoids the mental
// translation step on iPhone. Everyone else gets English defaults.
function isDutch() {
  const langs = navigator.languages || [navigator.language || ""];
  return langs.some((l) => (l || "").toLowerCase().startsWith("nl"));
}

const STRINGS = {
  en: {
    title: "Install Portside",
    subtitle_prompt: "Add to your home screen for one-tap access.",
    subtitle_ios: 'Tap the Share icon, then "Add to Home Screen".',
    install: "Install",
  },
  nl: {
    title: "Installeer Portside",
    subtitle_prompt: "Zet op beginscherm voor snelle toegang.",
    subtitle_ios: 'Tik op het Deel-symbool, kies "Zet op beginscherm".',
    install: "Installeer",
  },
};

export default function PwaInstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || isDismissed()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari one-off hint (no beforeinstallprompt event on iOS).
    if (isIOS()) {
      // Delay so we don't slap the user in the face on first paint.
      const t = setTimeout(() => setIosHint(true), 4000);
      return () => {
        window.removeEventListener("beforeinstallprompt", handler);
        clearTimeout(t);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(
        DISMISS_KEY,
        String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000)
      );
    } catch {
      // localStorage unavailable (private mode, etc.) — silently skip.
    }
    setVisible(false);
    setIosHint(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {
      // User dismissed or event stale — nothing to do.
    }
    setDeferred(null);
    setVisible(false);
  };

  if (!visible && !iosHint) return null;

  const t = isDutch() ? STRINGS.nl : STRINGS.en;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:hidden z-[70] bg-[#003147] text-white shadow-2xl rounded-lg border border-white/10 px-4 py-3 flex items-center gap-3 animate-slide-in-left"
      data-testid="pwa-install-banner"
    >
      <div className="w-10 h-10 rounded-md bg-white flex items-center justify-center p-1 shrink-0">
        <img src="/brand/portside-logo.svg" alt="" className="w-full h-full object-contain" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold leading-tight">{t.title}</div>
        <div className="text-[11px] text-gray-300 leading-snug mt-0.5">
          {visible ? t.subtitle_prompt : t.subtitle_ios}
        </div>
      </div>
      {visible && (
        <button
          onClick={install}
          className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#99D420] text-[#003147] text-xs font-bold hover:bg-[#AFCA0B] active:scale-95 transition"
          data-testid="pwa-install-btn"
        >
          <Download className="w-3.5 h-3.5" /> {t.install}
        </button>
      )}
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="shrink-0 p-1.5 rounded-md text-gray-300 hover:text-white hover:bg-white/10"
        data-testid="pwa-install-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
